import { useState, type FormEvent, type ReactNode } from "react";
import { TextInput } from "@mantine/core";
import { Button } from "./Button";
import { IconFilter, IconSearch } from "./icons";
import { STRINGS } from "./strings";

/* The chrome above a table: one search box that searches the whole table,
 * the funnel that opens the filters, every set filter as a chip, and the
 * table's own controls at the right. James, 2026-10-08: tabular data has
 * search, sort and filter by default, compact and well made, and the funnel
 * is the standard way in to the filters.
 *
 * A set filter is never hidden. The chips show whether the panel is open or
 * shut, because a narrowed list that looks unnarrowed is the screen lying
 * about its own contents. */
export type Chip = { label: string; onRemove: () => void };

export function Toolbar({
  search, filtersCount = 0, filtersOpen = false, onToggleFilters, chips = [], onClearAll, right, id,
}: {
  /** The search box. Absent means the table has no search behind it. */
  search?: { label: string; value: string; onSubmit: (q: string) => void };
  /** How many filters are set, shown on the funnel. */
  filtersCount?: number;
  filtersOpen?: boolean;
  /** Absent means the table has no filters. */
  onToggleFilters?: () => void;
  chips?: readonly Chip[];
  onClearAll?: () => void;
  /** The table's own controls: loaded-at, columns, download, refresh. */
  right?: ReactNode;
  /** A prefix for the search box's id, so two toolbars on a page differ. */
  id?: string;
}) {
  const searchId = `${id ?? "list"}-q`;
  const panelId = `${id ?? "list"}-filters`;
  const narrowed = chips.length > 0 || Boolean(search?.value);
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const input = e.currentTarget.elements.namedItem("q") as HTMLInputElement | null;
    search?.onSubmit(input?.value.trim() ?? "");
  };
  return (
    <div className="toolbar">
      {search ? (
        <form className="toolbar-search" onSubmit={submit} role="search">
          <label htmlFor={searchId} className="sr-only">{search.label}</label>
          {/* A visible label, small and beside the box, so a sighted person
              knows what the search covers before typing. A placeholder is
              not a label (docs/design-standards.md, Accessibility), and the
              label alone for screen readers left a sighted person guessing
              (the 2026-10-08 audit). */}
          <label htmlFor={searchId} className="toolbar-search-label muted">{search.label}</label>
          <TextInput
            id={searchId} name="q" defaultValue={search.value} key={search.value}
            leftSection={<IconSearch />} w="17rem"
          />
        </form>
      ) : null}
      {onToggleFilters ? (
        <Button
          size="compact-sm" className="toolbar-filters" onClick={onToggleFilters}
          aria-expanded={filtersOpen} aria-controls={panelId}
        >
          <IconFilter />
          <span className="toolbar-filters-word">{STRINGS.filters}{filtersCount ? ` (${filtersCount})` : ""}</span>
        </Button>
      ) : null}
      {chips.map((chip, i) => (
        <span className="chip" key={`${chip.label}-${i}`}>
          {chip.label}
          <button type="button" aria-label={`Remove the filter ${chip.label}`} onClick={chip.onRemove}>×</button>
        </span>
      ))}
      {narrowed && onClearAll ? (
        <Button kind="link" size="compact-xs" onClick={onClearAll}>{STRINGS.clearAll}</Button>
      ) : null}
      {right ? <span className="toolbar-right">{right}</span> : null}
    </div>
  );
}

/** The panel the funnel opens, under the toolbar. */
export function FiltersPanel({ id, open, onDone, children }: {
  id?: string; open: boolean; onDone: () => void; children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="panel-inline filters-panel" id={`${id ?? "list"}-filters`}>
      {children}
      <div className="bar filters-panel-foot">
        <Button size="compact-sm" onClick={onDone}>{STRINGS.done}</Button>
      </div>
    </div>
  );
}

/** Open or shut, remembered while the table is on screen. Opens on its own
 *  when a filter is already set, which is what a followed link does. */
export function useFiltersOpen(initiallySet: boolean) {
  return useState(initiallySet);
}
